import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccessGroup } from '../../../shared/classes/access-group';
import { AccessPolicy } from '../../../shared/classes/access-policy';
import { AuthLevel, AuthLevelPresentation } from '../../../shared/enums/auth-level.enum';
import { Application } from '../../../shared/classes/application';
import { DefaultDialogConfig } from '../../../shared/classes/default-dialog-config';
import { ResourceService } from '../../list/resource.service';
import { firstValueFrom } from 'rxjs';

interface CreateAccessPolicyDialogData {
  application?: Application;
  accessGroup?: AccessGroup;
}

export class CreateAccessPolicyDialogConfig extends DefaultDialogConfig<CreateAccessPolicyDialogData> {
  /**
   * Configure this dialog to create an access policy for an existing Application or Access Group.
   * @param creatingFor The application or access group this access policy is being created for.
   */
  constructor(creatingFor: Application | AccessGroup) {
    super();
    if (creatingFor instanceof Application) {
      this.data = { application: creatingFor };
    } else if (creatingFor instanceof AccessGroup) {
      this.data = { accessGroup: creatingFor };
    } else {
      throw Error('Must provide an existing Application or Access Group when creating an access policy');
    }
  }
}

enum ConnectingTarget {
  Application,
  AccessGroup,
}

@Component({
  selector: 'app-create-access-policy-dialog',
  templateUrl: './create-access-policy-dialog.component.html',
  styleUrls: ['./create-access-policy-dialog.component.scss'],
  standalone: false
})
export class CreateAccessPolicyDialogComponent implements OnInit {
  private readonly data = inject<CreateAccessPolicyDialogData>(MAT_DIALOG_DATA);
  private readonly matDialogRef = inject<MatDialogRef<CreateAccessPolicyDialogComponent>>(MatDialogRef);
  private readonly resources = inject(ResourceService);

  public AuthLevel = AuthLevel;
  public ConnectingTarget = ConnectingTarget;
  public accessGroups: AccessGroup[] = null;
  public applications: Application[] = null;
  public busy = false;
  public filteredWarning = false;
  public error = false;

  public readonly AuthLevelDisplay = AuthLevelPresentation;
  public readonly AuthLevels = Object.values(AuthLevel) as AuthLevel[];

  private application: Application;
  private accessGroup: AccessGroup;

  public readonly form = new FormGroup({
    authLevelControl: new FormControl(AuthLevel.Standard, [Validators.required]),
    applicationControl: new FormControl<string>(null, { nonNullable: false }),
    accessGroupControl: new FormControl<string>(null, { nonNullable: false }),
  });
  public get authLevelControl() { return this.form.controls.authLevelControl; }
  public get accessGroupControl() { return this.form.controls.accessGroupControl; }
  public get applicationControl() { return this.form.controls.applicationControl; }

  public get target(): ConnectingTarget {
    return this.application ? ConnectingTarget.AccessGroup : ConnectingTarget.Application;
  }

  public readonly ApplicationDisplay = (app: Application) => app.name;
  public readonly ApplicationValue = (app: Application) => app.id;

  public readonly AccessGroupDisplay = (group: AccessGroup) => group.name;
  public readonly AccessGroupValue = (group: AccessGroup) => group.id;

  public ngOnInit() {
    this.application = this.data.application;
    this.accessGroup = this.data.accessGroup;

    if (this.target === ConnectingTarget.AccessGroup) {
      this.accessGroupControl.setValidators(Validators.required);
      this.loadEligibleAccessGroups();
    } else {
      this.applicationControl.setValidators(Validators.required);
      this.loadEligibleApplications();
    }
  }

  public cancel(): void {
    this.matDialogRef.close();
  }

  public submit(): void {
    this.busy = true;
    this.error = false;
    this.form.disable();

    const policy = new AccessPolicy();
    policy.applicationId = this.application ? this.application.id : this.applicationControl.value;
    policy.groupId = this.accessGroup ? this.accessGroup.id : this.accessGroupControl.value;
    policy.authLevel = this.authLevelControl.value as AuthLevel;

    this.resources.createAccessPolicy(policy).subscribe(() => {
      this.matDialogRef.close(true);
    }, error => {
      console.error(`Failed to create access policy`, policy, error);
      this.busy = false;
      this.error = true;
      this.form.enable();
    });
  }

  /**
   * Loads only those access groups not already assigned to this application.
   */
  private async loadEligibleAccessGroups() {
    const [accessPolicies, allAccessGroups] = await Promise.all([
      firstValueFrom(this.resources.getAccessPoliciesForApplication(this.application.id), { defaultValue: undefined }),
      firstValueFrom(this.resources.getAccessGroups(), { defaultValue: undefined }),
    ]);

    const eligibleGroups = allAccessGroups.filter(group => !accessPolicies.find(policy => policy.groupId === group.id));

    if (eligibleGroups.length < allAccessGroups.length) {
      this.filteredWarning = true;
    }

    this.accessGroups = eligibleGroups;
  }

  /**
   * Loads only those applications not already assigned to this access group.
   */
  private async loadEligibleApplications(): Promise<void> {
    const [accessPolicies, allApplications] = await Promise.all([
      firstValueFrom(this.resources.getAccessPoliciesForGroup(this.accessGroup.id), { defaultValue: undefined }),
      firstValueFrom(this.resources.getApplicationList(), { defaultValue: undefined }),
    ]);

    const eligibleApplications = allApplications.filter(app => !accessPolicies.find(policy => policy.applicationId === app.id));

    if (eligibleApplications.length < allApplications.length) {
      this.filteredWarning = true;
    }

    this.applications = eligibleApplications;
  }

}
